import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ERROR_CODES,
  pickBilingual,
  type Locale,
  type PersonRef,
  type ReportDeliveryStatus,
  type ReportStatus,
  type ReportVersionStatus,
  type ResultCorrectionImpactState,
} from '@lims/shared-types';
import { ApiError } from '../../api/client.js';
import { isRenderingUnavailable } from '../../api/endpoints.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { formatDateTime } from '../../components/format.js';
import { Alert, StatusBadge, toneForStatus, type Tone } from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { isolateRuns, run } from '../settings/report-template-parts.js';

/**
 * Pieces shared by the report register, the preparation page, a report's own
 * page and the work-order file's reports section.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTIONS 4, 5 AND 8 BEFORE ADDING ANYTHING HERE.
 * ===========================================================================
 *
 * ISSUANCE IS NOT DELIVERY. Every place a report's status is shown, its
 * delivery state is shown beside it as a SEPARATE fact, so "issued and valid"
 * can never be read as "the client has it".
 *
 * APPROVAL IS NOT A PASS, AND A VALID QR IS NOT SCIENTIFIC CORRECTNESS. No
 * colour below is a verdict about a material: `issued_valid` is green because
 * the document is in force, nothing more.
 *
 * NOTHING HERE IS AUTHORIZATION. The `can.*` hints the API sends decide what is
 * OFFERED; every route re-checks the permission and the state on the server
 * (CLAUDE.md section 5.1).
 */

/** The query key every report read lives under, so one change refreshes all. */
export const REPORTS_QUERY_KEY = ['reports'] as const;

/** Detail codes a report endpoint names, each with a sentence in `reports.refusal`. */
export const REPORT_REFUSAL_CODES = [
  'result_already_reported',
  'partial_reports_not_allowed',
  'template_retired',
  'language_not_supported',
  'draft_changed',
  'correction_pending_reapproval',
  'report_rendering_unavailable',
  'verification_base_url_not_configured',
  'report_file_integrity_failed',
  // Sub-phase 10c: acts on an issued report, deliveries and the payment hold.
  'correction_already_open',
  'report_not_issued_valid',
  'report_not_suspended',
  'no_current_version',
  'report_not_deliverable',
  'payment_hold_not_held',
  'delivered_at_in_future',
  'client_contact_not_of_client',
  'status_event_not_of_issued_version',
  'result_not_in_report',
  // Issuance and previews: text the fonts cannot print (D135), the logo, races.
  'unsupported_characters',
  'logo_format_not_printable',
  'logo_file_integrity_failed',
  'report_changed',
  'no_open_draft',
  'result_not_approved',
] as const;

export type ReportRefusalCode = (typeof REPORT_REFUSAL_CODES)[number];

/**
 * The detail codes an API error carries. A code may travel as the detail's
 * `code` or as its `message`, so both are read.
 */
export function detailCodes(error: unknown): string[] {
  if (!(error instanceof ApiError)) return [];
  return error.details.flatMap((detail) =>
    [detail.code, detail.message].filter(
      (value): value is string => typeof value === 'string' && value !== '',
    ),
  );
}

export function hasRefusal(error: unknown, code: ReportRefusalCode): boolean {
  return detailCodes(error).includes(code);
}

/** Every string an error's details carry — used to find the attempt ids a refusal names. */
function detailValues(error: ApiError): string[] {
  return error.details.flatMap((detail) =>
    [detail.field, detail.code, detail.message].filter(
      (value): value is string => typeof value === 'string' && value !== '',
    ),
  );
}

/**
 * Refusals that say the report is no longer in the state the page showed —
 * somebody else acted first, a hold is in force, a result was reopened. The
 * page reloads the report after one of these, so what is behind the dialog is
 * what is really there.
 */
export function refusalCallsForReload(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === ERROR_CODES.INVALID_STATE_TRANSITION ||
      error.code === ERROR_CODES.CONFLICT ||
      error.code === ERROR_CODES.REPORT_ACCESS_HELD ||
      error.code === ERROR_CODES.RESULT_NOT_APPROVED)
  );
}

/** A code point as the API names it: `U+` and four to six hexadecimal digits. */
const CODE_POINT = /^U\+[0-9A-F]{4,6}$/i;

export interface UnprintableText {
  /** The document path the text sits at — `remarks`, `laboratory.nameEn`, … */
  readonly field: string | null;
  /** `U+XXXX`, as the API named them. */
  readonly characters: readonly string[];
}

/**
 * Where an `unsupported_characters` refusal (D135) says the document holds
 * characters its fonts cannot print. The detail's `field` is the document path
 * and its `message` the code points, space-separated. Only tokens shaped like a
 * code point are kept, so no other server text reaches the screen this way.
 */
export function unprintableText(error: unknown): UnprintableText[] {
  if (!(error instanceof ApiError)) return [];
  return error.details
    .filter((detail) => detail.code === 'unsupported_characters')
    .map((detail) => ({
      field: typeof detail.field === 'string' && detail.field !== '' ? detail.field : null,
      characters: (detail.message ?? '')
        .split(/\s+/)
        .filter((token) => CODE_POINT.test(token))
        .map((token) => token.toUpperCase()),
    }));
}

/** Refusals whose details name attempts, which the page can turn into test and sample. */
const ATTEMPT_NAMING_REFUSALS: readonly ReportRefusalCode[] = [
  'result_not_in_report',
  'correction_pending_reapproval',
];

function NamedAttempts({
  error,
  describeAttempt,
}: {
  readonly error: ApiError;
  readonly describeAttempt: ((attemptId: string) => ReactNode | null) | undefined;
}) {
  if (!describeAttempt) return null;
  const named = [...new Set(detailValues(error))]
    .map((value) => ({ value, label: describeAttempt(value) }))
    .filter((entry) => entry.label !== null);
  if (named.length === 0) return null;
  return (
    <ul className="flex list-disc flex-col gap-1 ps-6 text-body text-default-800">
      {named.map((entry) => (
        <li key={entry.value}>{entry.label}</li>
      ))}
    </ul>
  );
}

/**
 * A refusal from a report endpoint, in words.
 *
 * A named refusal the catalogue knows is said in its own sentence; a result
 * that is no longer approved — or that a correction names wrongly — is NAMED,
 * so the person knows which one; text the report fonts cannot print is shown
 * with WHERE it is and WHICH code points, so it can be corrected; anything else
 * falls back to the stable error code's message. Never the server's own
 * English.
 */
export function ReportRefusalNotice({
  error,
  describeAttempt,
}: {
  readonly error: unknown;
  /** Turns an attempt id the refusal names into the test and sample it is. */
  readonly describeAttempt?: (attemptId: string) => ReactNode | null;
}) {
  const { t } = useTranslation();
  if (!error) return null;

  if (error instanceof ApiError && error.code === ERROR_CODES.RESULT_NOT_APPROVED) {
    return (
      <div className="flex flex-col gap-inline">
        <Alert tone="danger" title={t('errors.RESULT_NOT_APPROVED')} />
        <NamedAttempts error={error} describeAttempt={describeAttempt} />
      </div>
    );
  }

  // Access held until payment is recorded: a restriction, not a fault in the report.
  if (error instanceof ApiError && error.code === ERROR_CODES.REPORT_ACCESS_HELD) {
    return <Alert tone="warning" title={t('errors.REPORT_ACCESS_HELD')} />;
  }

  // Installation problems: nothing was written, and nothing is wrong with the draft.
  if (isRenderingUnavailable(error)) {
    return <Alert tone="warning" title={t('reports.refusal.report_rendering_unavailable')} />;
  }
  if (hasRefusal(error, 'verification_base_url_not_configured')) {
    return (
      <Alert tone="warning" title={t('reports.refusal.verification_base_url_not_configured')} />
    );
  }

  // WHERE the unprintable text is and WHICH characters, each run isolated: a
  // Latin path and code points inside an Arabic sentence stay readable.
  const unprintable = unprintableText(error);
  if (unprintable.length > 0) {
    return (
      <div className="flex flex-col gap-inline" data-testid="unsupported-characters">
        <Alert tone="danger" title={t('reports.refusal.unsupported_characters')} />
        <ul className="flex list-disc flex-col gap-1 ps-6 text-body text-default-800">
          {unprintable.map((entry, index) => (
            <li key={`${entry.field ?? ''}-${index}`}>
              {isolateRuns(
                t('reports.refusalDetail.charactersIn', {
                  field: run('field'),
                  characters: run('characters'),
                }),
                {
                  field: entry.field ? (
                    <code className="font-mono text-caption" dir="ltr">
                      {entry.field}
                    </code>
                  ) : (
                    t('common.notRecorded')
                  ),
                  characters:
                    entry.characters.length > 0 ? (
                      <code className="font-mono text-caption" dir="ltr">
                        {entry.characters.join(' ')}
                      </code>
                    ) : (
                      t('common.notRecorded')
                    ),
                },
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const codes = detailCodes(error);
  const refusal = REPORT_REFUSAL_CODES.find((code) => codes.includes(code));
  if (refusal) {
    return (
      <div className="flex flex-col gap-inline">
        <Alert tone="danger" title={t(`reports.refusal.${refusal}`)} />
        {error instanceof ApiError && ATTEMPT_NAMING_REFUSALS.includes(refusal) ? (
          <NamedAttempts error={error} describeAttempt={describeAttempt} />
        ) : null}
      </div>
    );
  }
  return <ErrorNotice error={error} />;
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

/** Delivery states are facts about hand-over, never about validity. */
const DELIVERY_TONES: Record<ReportDeliveryStatus, Tone> = {
  // Issued and ready is not "with the client": said as a caution, not a failure.
  not_released: 'warning',
  available_to_client: 'info',
  sent: 'info',
  receipt_confirmed: 'success',
};

export function ReportStatusBadge({ status }: { readonly status: ReportStatus }) {
  const { t } = useTranslation();
  return <StatusBadge label={t(`status.report.${status}`)} tone={toneForStatus(status)} />;
}

export function VersionStatusBadge({ status }: { readonly status: ReportVersionStatus }) {
  const { t } = useTranslation();
  return (
    <StatusBadge label={t(`status.reportVersion.${status}`)} tone={toneForStatus(status)} />
  );
}

export function DeliveryStatusBadge({ status }: { readonly status: ReportDeliveryStatus }) {
  const { t } = useTranslation();
  return (
    <StatusBadge
      label={t(`status.reportDelivery.${status}`)}
      tone={DELIVERY_TONES[status] ?? 'neutral'}
    />
  );
}

const CORRECTION_TONES: Record<ResultCorrectionImpactState, Tone> = {
  none: 'neutral',
  correction_open: 'warning',
  replacement_required: 'warning',
  concluded_without_change: 'info',
};

/** A reported result's state since it was reported (D117) — shown, never acted on. */
export function ResultCorrectionState({
  state,
}: {
  readonly state: ResultCorrectionImpactState;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <StatusBadge
        label={t(`resultCorrectionImpact.${state}`)}
        tone={CORRECTION_TONES[state] ?? 'neutral'}
      />
      <p className="text-caption text-default-600">{t(`resultCorrectionImpactHint.${state}`)}</p>
    </div>
  );
}

/**
 * The report number, or the plain statement that a number is allocated at
 * first issuance (D113). A machine identifier reads left to right in both
 * languages.
 */
export function ReportNumber({ number }: { readonly number: string | null }) {
  const { t } = useTranslation();
  return number ? (
    <span className="measure" dir="ltr">
      {number}
    </span>
  ) : (
    <span>{t('reports.numberPending')}</span>
  );
}

// ---------------------------------------------------------------------------
// Names, dates and titles
// ---------------------------------------------------------------------------

export function bilingual(
  value: { readonly ar: string | null; readonly en: string | null } | null | undefined,
  locale: Locale,
): string | null {
  if (!value) return null;
  return pickBilingual({ ar: value.ar, en: value.en }, locale);
}

/**
 * A person and a moment, each in its own isolated run.
 *
 * A Latin name beside an Arabic-formatted date is exactly where the
 * bidirectional algorithm drags the date's digits into the name. `<bdi>` keeps
 * both readable, and the date is never forced left to right: an Arabic date
 * stays an Arabic date.
 */
export function PersonAt({
  person,
  at,
}: {
  readonly person: PersonRef | null;
  readonly at?: string | null;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const name = person?.displayName ?? t('common.notRecorded');
  const date = at === undefined ? null : (formatDateTime(at, { locale }) ?? t('common.notRecorded'));
  return (
    <>
      <bdi>{name}</bdi>
      {date === null ? null : (
        <>
          {' · '}
          <bdi>{date}</bdi>
        </>
      )}
    </>
  );
}

/** A moment on its own, isolated. */
export function MomentText({ at }: { readonly at: string | null }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  return <bdi>{formatDateTime(at, { locale }) ?? t('common.notRecorded')}</bdi>;
}

/** Test names only, each isolated — a register says what was tested, never what was found. */
export function TestTitles({
  titles,
}: {
  readonly titles: readonly { readonly ar: string | null; readonly en: string | null }[];
}) {
  const locale = currentLocale();
  const names = titles
    .map((title) => bilingual(title, locale))
    .filter((name): name is string => Boolean(name));
  return (
    <span className="block min-w-0 text-default-700">
      {names.map((name, index) => (
        <Fragment key={`${name}-${index}`}>
          {index > 0 ? ' · ' : null}
          <bdi>{name}</bdi>
        </Fragment>
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------

/** Open PDF bytes in a new tab. The object URL is released once the tab has it. */
export function openPdf(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Hand PDF bytes to the browser's own print dialog, without leaving the page.
 * The PDF is loaded into an invisible frame and printed from there; where a
 * browser refuses that, it opens in a tab to be printed by hand instead.
 */
export function printPdf(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.style.cssText =
    'position:fixed;inset-inline-end:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } catch {
      openPdf(blob);
    }
  };
  frame.src = url;
  document.body.appendChild(frame);
  window.setTimeout(() => {
    frame.remove();
    URL.revokeObjectURL(url);
  }, 120_000);
}

/** Save bytes under a name. The name is the report number and version — never a storage key. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Anything but a PDF is not the document, whatever answered the request. */
export function requirePdf(blob: Blob): Blob {
  if (!blob.type.startsWith('application/pdf')) {
    throw new ApiError(ERROR_CODES.INTERNAL_ERROR, 0, 'The response was not a PDF.');
  }
  return blob;
}
